import { dayjs } from "@volca/ui/lib/date";
export { dayjs };

// Date format constants
export const FORMAT_SIMPLE_MONTH_DAY = "M/D";
export const FORMAT_FILENAME_DATE = "YYYY-MM-DD";
export const FORMAT_DATE_PICKER_V2 = "PPP"; // For date picker v2 button display

/**
 * Gets the day of the week or date string for a given date
 *
 * @param date - Date string, Date object, or null
 */
const getDateString = (date?: string | Date | null) => {
  if (!date) return "No date";

  const inputDate = dayjs(date);
  const now = dayjs();

  if (inputDate.isSame(now, "day")) return "Today";
  if (inputDate.isSame(now.subtract(1, "day"), "day")) return "Yesterday";

  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  if (inputDate.isSame(now, "week")) return days[inputDate.day()];
  return inputDate.format("MMM DD");
};

/**
 * Formats a date string or Date object into a readable date format
 *
 * Format string: "MMM DD, YYYY"
 *
 * Example: "Dec 01, 2024"
 *
 * @param date - Date string, Date object, or null
 */
export const formatDate = (date?: string | Date | null) =>
  date ? dayjs(date).format("MMM DD, YYYY") : "No date";

/**
 * Formats a date string or Date object into a readable date and time format
 *
 *
 *
 * @param date - Date string, Date object, or null
 */
export const formatDateAndTime = (date?: string | Date | null) =>
  date ? dayjs(date).format("MMM D, YYYY, h:mm a") : "No time";

/**
 * Formats a date string or Date object into a readable date and time format
 *
 * Format string: "MMM DD, h:mm a"
 *
 * Example: "Mar 21, 10:00 am"
 *
 * @param date - Date string, Date object, or null
 */
export const formatTime = (date?: string | Date | null) =>
  date ? dayjs(date).format("MMM DD, h:mm a") : "No time";

/**
 * Formats a date string or Date object into a readable date and time format with timezone
 *
 * Format string: "MMM DD, YYYY - h:mm A z"
 *
 * Example: "Jan 01, 2024 - 10:02 AM PST"
 *
 * @param date - Date string, Date object, or null
 */
export const formatDateTimeWithZone = (date?: string | Date | null) =>
  date
    ? `${dayjs(date).tz(dayjs.tz.guess()).format("MMM DD, YYYY - h:mm A z")}`
    : "No date";

/**
 * Formats a date string or Date object into a relative time string
 *
 * Examples: "2 days ago", "Last week", "2 months ago", "Last year"
 *
 * @param date - Date string, Date object, or null
 */
export const formatRelativeDate = (date?: string | Date | null) => {
  if (!date) return "No date";

  // Use calendar days, not 24-hour periods
  const now = dayjs().startOf("day");
  const then = dayjs(date).startOf("day");
  const diff = now.diff(then, "day");

  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return `${diff} days ago`;
  if (diff < 14) return "Last week";
  if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;
  if (diff < 60) return "Last month";
  if (diff < 365) return `${Math.floor(diff / 30)} months ago`;
  if (diff < 730) return "Last year";
  return `${Math.floor(diff / 365)} years ago`;
};

/**
 * Returns the difference in calendar days between two dates
 *
 * Formats a date string or Date object into a relative time string
 *
 * Examples: "Today, 11:00 AM", "Yesterday, 11:00 AM", "Monday, 11:00 AM"
 *
 * @param date - Date string, Date object, or null
 */
export const formatRelativeTime = (date?: string | Date | null) => {
  if (!date) return "No time";

  const inputDate = dayjs(date);
  const now = dayjs();

  if (inputDate.isSame(now, "year")) {
    return `${getDateString(date)}, ${inputDate.format("h:mm a")}`;
  } else {
    return formatDate(date);
  }
};

// Move dateUtils export to the end
export const dateUtils = {
  /**
   * Checks if a given date is today
   * @param date - Date string or Date object
   * @returns boolean indicating if the date is today
   */
  isToday: (date: string | Date) => dayjs(date).isSame(dayjs(), "day"),

  /**
   * Checks if a given date is in the future
   * @param date - Date string or Date object
   * @returns boolean indicating if the date is in the future
   */
  isFuture: (date: string | Date) => dayjs(date).isAfter(dayjs()),

  /**
   * Checks if a given date is in the past
   * @param date - Date string or Date object
   * @returns boolean indicating if the date is in the past
   */
  isPast: (date: string | Date) => dayjs(date).isBefore(dayjs()),

  /**
   * Adds a specified number of days to a given date
   * @param date - Date string or Date object
   * @param days - Number of days to add
   * @returns New Date object with the specified number of days added
   */
  addDays: (date: string | Date, days: number) =>
    dayjs(date).add(days, "day").toDate(),
};

/**
 *
 *
 * @param start - Start date string (YYYY-MM-DD)
 * @param end - End date string (YYYY-MM-DD)
 * @returns Formatted range string
 */
export const formatDateRange = (start: string, end: string): string => {
  const from = dayjs(start);
  const to = dayjs(end);
  const endLabel = to.format("MMM D, YYYY");

  if (from.isSame(to, "day")) return endLabel;
  if (!from.isSame(to, "year"))
    return `${from.format("MMM D, YYYY")}–${endLabel}`;
  if (!from.isSame(to, "month")) return `${from.format("MMM D")}–${endLabel}`;
  return `${from.format("MMM D")}–${to.format("D, YYYY")}`;
};

/**
 * Sorts two date strings in descending order (most recent first)
 * @param dateA - First date string or null/undefined
 * @param dateB - Second date string or null/undefined
 * @returns Number for array sort comparison
 */
export const sortDateDescending = (
  dateA?: string | null,
  dateB?: string | null,
): number => {
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;

  return dayjs(dateB).valueOf() - dayjs(dateA).valueOf();
};

/**
 * Sorts two date strings in ascending order (oldest first)
 * @param dateA - First date string or null/undefined
 * @param dateB - Second date string or null/undefined
 * @returns Number for array sort comparison
 */
export const sortDateAscending = (
  dateA?: string | null,
  dateB?: string | null,
): number => {
  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;

  return dayjs(dateA).valueOf() - dayjs(dateB).valueOf();
};

/**
 * Calculates start and end dates based on a time frame identifier
 * @param timeFrame - Time frame identifier (e.g., 'last_thirty_days', 'all_time', 'week_1')
 * @returns Object containing start and end dates as dayjs objects, or null for 'all_time'
 */
export const getDateRangeFromTimeFrame = (
  timeFrame?: string,
): { startDate: dayjs.Dayjs; endDate: dayjs.Dayjs } | null => {
  if (!timeFrame) return null;

  const now = dayjs();

  switch (timeFrame) {
    case "today": {
      const startDate = now.startOf("day");
      const endDate = now.endOf("day");
      return { startDate, endDate };
    }

    case "last_thirty_days": {
      const start = now.subtract(30, "day").startOf("day");
      return { startDate: start, endDate: now.endOf("day") };
    }

    case "last_month": {
      const lastMonth = now.subtract(1, "month");
      const startDate = lastMonth.startOf("month");
      const endDate = lastMonth.endOf("month");
      return { startDate, endDate };
    }

    case "all_time":
      return null;

    default: {
      if (!timeFrame.startsWith("week_")) return null;

      const weekNumber = parseInt(timeFrame.split("_")[1], 10);
      if (!Number.isInteger(weekNumber) || weekNumber <= 0) return null;

      const currentDay = now.day();
      const daysToLastMonday = currentDay === 0 ? 6 : currentDay - 1;
      const lastMonday = now.subtract(daysToLastMonday, "day").startOf("day");

      const weekStart = lastMonday
        .subtract((weekNumber - 1) * 7, "day")
        .startOf("day");
      const weekEnd = weekStart.add(6, "day").endOf("day");

      return { startDate: weekStart, endDate: weekEnd };
    }
  }
};
